import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebaseConfig';
import { IoAdd, IoTrash, IoImage, IoClose, IoEye, IoEyeOff } from 'react-icons/io5';
import { toast } from 'react-toastify';

interface Brand {
  id: string;
  name: string;
  logoUrl: string;
  isActive: boolean;
  order?: number;
  createdAt: any;
}

export const BrandManager: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  
  // Form states
  const [brandName, setBrandName] = useState('');
  const [brandOrder, setBrandOrder] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');

  // Fetch brands
  useEffect(() => {
    const brandsRef = collection(db, 'brands');
    
    const unsubscribe = onSnapshot(brandsRef, (snapshot) => {
      const brandsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Brand[];

      brandsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setBrands(brandsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const storageRef = ref(storage, `brands/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setLogoUrl(url);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error('Error uploading logo:', err);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAddBrand = async () => {
    if (!brandName.trim() || !logoUrl) {
      toast.error('Please provide brand name and logo');
      return;
    }

    try {
      const brandData = {
        name: brandName.trim(),
        logoUrl: logoUrl,
        isActive: true,
        order: brandOrder ? parseInt(brandOrder) : brands.length + 1,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'brands'), brandData);
      toast.success(`Brand "${brandName}" added successfully`);
      
      // Reset form
      setShowAddModal(false);
      setBrandName('');
      setBrandOrder('');
      setLogoUrl('');
    } catch (err) {
      console.error('Error adding brand:', err);
      toast.error('Failed to add brand');
    }
  };

  const handleUpdateBrand = async () => {
    if (!editingBrand || !brandName.trim() || !logoUrl) {
      toast.error('Please provide brand name and logo');
      return;
    }

    try {
      const updateData = {
        name: brandName.trim(),
        logoUrl: logoUrl,
        order: brandOrder ? parseInt(brandOrder) : editingBrand.order,
      };

      await updateDoc(doc(db, 'brands', editingBrand.id), updateData);
      toast.success('Brand updated successfully');
      
      setShowEditModal(false);
      setEditingBrand(null);
      setBrandName('');
      setBrandOrder('');
      setLogoUrl('');
    } catch (err) {
      console.error('Error updating brand:', err);
      toast.error('Failed to update brand');
    }
  };

  const handleToggleActive = async (brand: Brand) => {
    try {
      await updateDoc(doc(db, 'brands', brand.id), {
        isActive: !brand.isActive,
      });
      toast.success(`Brand ${brand.isActive ? 'disabled' : 'enabled'}`);
    } catch (err) {
      console.error('Error toggling brand:', err);
      toast.error('Failed to update brand status');
    }
  };

  const handleDeleteBrand = async (brand: Brand) => {
    if (!window.confirm(`Delete "${brand.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'brands', brand.id));
      toast.success('Brand deleted successfully');
    } catch (err) {
      console.error('Error deleting brand:', err);
      toast.error('Failed to delete brand');
    }
  };

  const openEditModal = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandName(brand.name);
    setBrandOrder(brand.order?.toString() || '');
    setLogoUrl(brand.logoUrl);
    setShowEditModal(true);
  };

  if (loading) {
    return <div className="text-center py-8">Loading brands...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-zinc-800">Brand Management</h2>
          <p className="text-gray-600">Manage affiliated brands displayed on the website</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-all shadow-md"
        >
          <IoAdd size={20} />
          Add Brand
        </button>
      </div>

      {/* Brands Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brands.map((brand) => (
          <div
            key={brand.id}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-zinc-800">{brand.name}</h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  brand.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {brand.isActive ? 'Active' : 'Disabled'}
              </span>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4 h-32 flex items-center justify-center">
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Order: {brand.order || 'N/A'}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => openEditModal(brand)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <IoImage size={18} />
                Edit
              </button>
              <button
                onClick={() => handleToggleActive(brand)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  brand.isActive
                    ? 'bg-orange-600 text-white hover:bg-orange-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {brand.isActive ? <IoEyeOff size={18} /> : <IoEye size={18} />}
                {brand.isActive ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => handleDeleteBrand(brand)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <IoTrash size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {brands.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="max-w-md mx-auto space-y-4">
            <div className="text-6xl">🏢</div>
            <h3 className="text-xl font-bold text-zinc-800">No Brands Yet</h3>
            <p className="text-gray-600">
              Get started by adding your first brand.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-all shadow-md mx-auto"
            >
              <IoAdd size={20} />
              Add Your First Brand
            </button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-zinc-800">Add New Brand</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setBrandName('');
                  setBrandOrder('');
                  setLogoUrl('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IoClose size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="e.g., Acer"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order (optional)
                </label>
                <input
                  type="number"
                  value={brandOrder}
                  onChange={(e) => setBrandOrder(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="1, 2, 3..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Logo *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  disabled={uploadingLogo}
                />
                {uploadingLogo && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                {logoUrl && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <img src={logoUrl} alt="Preview" className="max-h-32 mx-auto object-contain" />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setBrandName('');
                  setBrandOrder('');
                  setLogoUrl('');
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBrand}
                disabled={!brandName.trim() || !logoUrl || uploadingLogo}
                className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Add Brand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-zinc-800">Edit Brand</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingBrand(null);
                  setBrandName('');
                  setBrandOrder('');
                  setLogoUrl('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IoClose size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order (optional)
                </label>
                <input
                  type="number"
                  value={brandOrder}
                  onChange={(e) => setBrandOrder(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Logo *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  disabled={uploadingLogo}
                />
                {uploadingLogo && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                {logoUrl && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <img src={logoUrl} alt="Preview" className="max-h-32 mx-auto object-contain" />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingBrand(null);
                  setBrandName('');
                  setBrandOrder('');
                  setLogoUrl('');
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBrand}
                disabled={!brandName.trim() || !logoUrl || uploadingLogo}
                className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
